import { useState, useEffect, useRef } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const inputStyle = {
  width: '100%', padding: '14px 16px', borderRadius: 12, fontSize: 15,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.07)', color: '#fff',
  outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };
const labelStyle = { display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 6, fontWeight: 700, letterSpacing: '0.02em' };

const COMPANY_TYPES = [
  { value: 'cit8', label: 'sp. z o.o. (CIT-8)' },
  { value: 'kpir', label: 'JDG — KPiR' },
  { value: 'ryczalt', label: 'JDG — Ryczałt' },
];

// STATUS_LABELS moved inside component to use t()

function Section({ title, children }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)', borderRadius: 16, padding: '18px 16px', marginBottom: 16,
      border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}>
      <div style={{ fontWeight: 800, marginBottom: 14, fontSize: 16, letterSpacing: '0.01em' }}>{title}</div>
      {children}
    </div>
  );
}

export default function AccountingRestore() {
  const { user, setPage, currentCompany } = useApp();
  const { t } = useI18n();
  const STATUS_LABELS = {
    pending: `⏳ ${t('accounting_restore.status_pending')}`,
    downloading: `📥 ${t('accounting_restore.status_downloading')}`,
    parsing: `🔍 ${t('accounting_restore.status_parsing')}`,
    questions: `❓ ${t('accounting_restore.status_questions')}`,
    completed: `✅ ${t('accounting_restore.status_completed')}`,
    error: `❌ ${t('accounting_restore.status_error')}`,
  };
  const [tab, setTab] = useState('form'); // form | progress | results | package | package_result
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [results, setResults] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [answeringId, setAnsweringId] = useState(null);
  const pollRef = useRef(null);
  const [pkgJobId, setPkgJobId] = useState(null);
  const [pkgStatus, setPkgStatus] = useState(null);
  const [pkgLoading, setPkgLoading] = useState(false);
  const pkgPollRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    google_drive_url: '',
    company_type: 'cit8',
    period_mode: 'year', // year | range
    year: currentYear,
    period_start: `${currentYear}-01-01`,
    period_end: `${currentYear}-12-31`,
  });

  function upd(k, v) { setForm(f => ({ ...f, [k]: v })); }

  // Poll job status
  useEffect(() => {
    if (tab === 'progress' && jobId) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/owner/accounting/restore/${jobId}`);
          setJob(data);
          if (['completed', 'error', 'questions'].includes(data.status)) {
            clearInterval(pollRef.current);
          }
        } catch {}
      }, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [tab, jobId]);

  async function startRestore() {
    if (!form.google_drive_url) { setMsg(`❌ ${t('accounting_restore.insert_link')}`); return; }

    const period_start = form.period_mode === 'year' ? `${form.year}-01-01` : form.period_start;
    const period_end = form.period_mode === 'year' ? `${form.year}-12-31` : form.period_end;

    setLoading(true); setMsg('');
    try {
      const { data } = await api.post('/owner/accounting/restore', {
        google_drive_url: form.google_drive_url,
        company_type: form.company_type,
        company_id: currentCompany?.id || null,
        period_start,
        period_end,
      });
      setJobId(data.job_id);
      setTab('progress');
      // Fetch initial status
      const statusRes = await api.get(`/owner/accounting/restore/${data.job_id}`);
      setJob(statusRes.data);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }

  async function submitAnswer(questionId) {
    if (!answerText.trim()) return;
    setAnsweringId(questionId);
    try {
      await api.post(`/owner/accounting/restore/${jobId}/answer`, {
        question_id: questionId,
        answer: answerText.trim(),
      });
      setAnswerText('');
      // Refresh job
      const { data } = await api.get(`/owner/accounting/restore/${jobId}`);
      setJob(data);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    } finally { setAnsweringId(null); }
  }

  async function loadResults() {
    try {
      const { data } = await api.get(`/owner/accounting/restore/${jobId}/results`);
      setResults(data);
      setTab('results');
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    }
  }

  // Package generation
  async function startPackage() {
    setPkgLoading(true); setMsg('');
    try {
      const { data } = await api.post('/owner/accounting/generate-full-package', {
        company_id: currentCompany?.id || 4,
        year: form.year || new Date().getFullYear(),
      });
      setPkgJobId(data.job_id);
      setTab('package');
      // Start polling
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    } finally { setPkgLoading(false); }
  }

  useEffect(() => {
    if (tab === 'package' && pkgJobId) {
      pkgPollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/owner/accounting/generate-full-package/${pkgJobId}`);
          setPkgStatus(data);
          if (['completed', 'error'].includes(data.status)) {
            clearInterval(pkgPollRef.current);
          }
        } catch {}
      }, 2000);
      return () => clearInterval(pkgPollRef.current);
    }
  }, [tab, pkgJobId]);

  // ═══════════════════════════════════════════════════
  // TAB: PACKAGE (progress + results)
  // ═══════════════════════════════════════════════════
  if (tab === 'package') {
    const s = pkgStatus;
    const done = s?.status === 'completed';
    const err = s?.status === 'error';
    const stats = s?.stats;
    return (
      <div className="page">
        <BackButton onClick={() => { setTab('form'); clearInterval(pkgPollRef.current); }} />
        <h1>📦 {t('accounting_restore.full_package')}</h1>
        {msg && <div className="message" style={{ marginBottom: 12 }}>{msg}</div>}

        <Section title={`🔄 ${t('accounting_restore.status')}`}>
          <div style={{
            padding: 16, borderRadius: 12, textAlign: 'center',
            background: err ? 'rgba(239,68,68,0.1)' : done ? 'rgba(16,185,129,0.1)' : 'rgba(99,102,241,0.1)',
            border: `1px solid ${err ? 'rgba(239,68,68,0.3)' : done ? 'rgba(16,185,129,0.3)' : 'rgba(99,102,241,0.3)'}`,
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>
              {done ? '✅' : err ? '❌' : '⏳'}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {s?.progress || t('accounting_restore.starting')}
            </div>
            {err && <div style={{ fontSize: 13, color: '#f87171', marginTop: 8 }}>{s.error}</div>}
          </div>
        </Section>

        {done && stats && (
          <>
            <Section title={`📊 ${t('accounting_restore.results')}`}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                {[
                  { label: t('accounting_restore.invoices_purchase'), value: stats.invoices_purchase, color: '#f87171' },
                  { label: t('accounting_restore.invoices_sale'), value: stats.invoices_sale, color: '#10b981' },
                  { label: t('accounting_restore.journal_entries'), value: stats.journal_entries, color: '#818cf8' },
                  { label: t('accounting_restore.employees_found'), value: stats.employees?.length || 0, color: '#f59e0b' },
                  { label: t('accounting_restore.zus_payments'), value: stats.zus_payments, color: '#06b6d4' },
                  { label: t('accounting_restore.tax_payments'), value: stats.tax_payments, color: '#ec4899' },
                  { label: t('accounting_restore.bank_fees'), value: stats.bank_fees, color: '#8b5cf6' },
                  { label: t('accounting_restore.internal_transfers'), value: stats.internal_transfers, color: '#64748b' },
                ].map((item, i) => (
                  <div key={i} style={{
                    padding: 14, borderRadius: 12,
                    background: `${item.color}11`, border: `1px solid ${item.color}33`,
                  }}>
                    <div style={{ fontSize: 11, opacity: 0.6 }}>{item.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: item.color }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </Section>

            {stats.employees?.length > 0 && (
              <Section title={`👥 ${t('accounting_restore.found_employees')}`}>
                {stats.employees.map((name, i) => (
                  <div key={i} style={{ padding: 8, fontSize: 14 }}>👤 {name}</div>
                ))}
              </Section>
            )}
          </>
        )}

        <button onClick={() => { setTab('form'); setPkgJobId(null); setPkgStatus(null); }} style={{
          width: '100%', padding: 10, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
          background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', marginTop: 16, marginBottom: 32,
        }}>← {t('common.back')}</button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // TAB: RESULTS
  // ═══════════════════════════════════════════════════
  if (tab === 'results' && results) return (
    <div className="page">
      <BackButton onClick={() => setTab('progress')} />
      <h1>📊 {t('accounting_restore.results_title')}</h1>
      {msg && <div className="message" style={{ marginBottom: 12 }}>{msg}</div>}

      {/* Summary */}
      <Section title={`📈 ${t('accounting_restore.summary')}`}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          <div style={{ padding: 14, borderRadius: 12, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('accounting_restore.income_label')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#10b981' }}>
              {(results.summary?.total_income || 0).toLocaleString()} PLN
            </div>
          </div>
          <div style={{ padding: 14, borderRadius: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('accounting_restore.expenses_label')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#f87171' }}>
              {(results.summary?.total_expenses || 0).toLocaleString()} PLN
            </div>
          </div>
          <div style={{ padding: 14, borderRadius: 12, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('accounting_restore.vat_due')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>
              {(results.summary?.vat_due || 0).toLocaleString()} PLN
            </div>
          </div>
          <div style={{ padding: 14, borderRadius: 12, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)' }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('accounting_restore.profit_label')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#818cf8' }}>
              {(results.summary?.profit || 0).toLocaleString()} PLN
            </div>
          </div>
        </div>
        {results.summary?.note && (
          <div style={{ marginTop: 12, fontSize: 13, opacity: 0.6, fontStyle: 'italic' }}>
            ℹ️ {results.summary.note}
          </div>
        )}
      </Section>

      {/* Invoices */}
      <Section title={`📄 ${t('accounting_restore.invoices_title')} (${results.invoices_created || results.invoices?.length || 0})`}>
        {results.invoices?.length > 0 ? results.invoices.map((inv, i) => (
          <div key={i} style={{
            padding: 12, borderRadius: 10, marginBottom: 8,
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.number || `${t('accounting_restore.invoices_title')} ${i + 1}`}</div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>{inv.seller} → {inv.buyer}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#10b981' }}>{inv.amount} PLN</div>
          </div>
        )) : (
          <div style={{ textAlign: 'center', padding: 20, opacity: 0.5, fontSize: 14 }}>
            {t('accounting_restore.invoices_after')}
          </div>
        )}
      </Section>

      {/* Transactions */}
      <Section title={`🏦 ${t('accounting_restore.transactions_title')} (${results.transactions_created || results.transactions?.length || 0})`}>
        {results.transactions?.length > 0 ? results.transactions.map((tx, i) => (
          <div key={i} style={{
            padding: 12, borderRadius: 10, marginBottom: 8,
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13 }}>{tx.description}</span>
              <span style={{ fontWeight: 700, color: tx.amount >= 0 ? '#10b981' : '#f87171' }}>
                {tx.amount >= 0 ? '+' : ''}{tx.amount} {tx.currency || 'PLN'}
              </span>
            </div>
            <div style={{ fontSize: 11, opacity: 0.5 }}>{tx.date}</div>
          </div>
        )) : (
          <div style={{ textAlign: 'center', padding: 20, opacity: 0.5, fontSize: 14 }}>
            {t('accounting_restore.transactions_after')}
          </div>
        )}
      </Section>

      {/* Generate report button */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
        <button className="btn btn-primary" style={{
          padding: 16, fontSize: 16, fontWeight: 800, borderRadius: 14,
          opacity: 0.5, cursor: 'not-allowed',
        }} disabled>
          📋 Сгенерировать {form.company_type === 'cit8' ? 'CIT-8' : form.company_type === 'kpir' ? 'PIT-36' : 'PIT-28'} (скоро)
        </button>
        <button onClick={() => { setTab('form'); setJob(null); setJobId(null); setResults(null); }} style={{
          padding: 10, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
          background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
        }}>🔄 {t('accounting_restore.new_restore')}</button>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════
  // TAB: PROGRESS
  // ═══════════════════════════════════════════════════
  if (tab === 'progress') return (
    <div className="page">
      <BackButton onClick={() => setTab('form')} />
      <h1>📊 {t('accounting_restore.title')}</h1>
      {msg && <div className="message" style={{ marginBottom: 12 }}>{msg}</div>}

      {/* Status */}
      <Section title={`🔄 ${t('accounting_restore.status')}`}>
        <div style={{
          padding: 16, borderRadius: 12, textAlign: 'center',
          background: job?.status === 'error' ? 'rgba(239,68,68,0.1)' :
                      job?.status === 'completed' ? 'rgba(16,185,129,0.1)' :
                      'rgba(99,102,241,0.1)',
          border: `1px solid ${job?.status === 'error' ? 'rgba(239,68,68,0.3)' :
                                job?.status === 'completed' ? 'rgba(16,185,129,0.3)' :
                                'rgba(99,102,241,0.3)'}`,
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>
            {job?.status === 'completed' ? '✅' : job?.status === 'error' ? '❌' : '⏳'}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>
            {STATUS_LABELS[job?.status] || job?.status || t('common.loading')}
          </div>
          {job?.error_message && (
            <div style={{ fontSize: 13, color: '#f87171', marginTop: 8 }}>{job.error_message}</div>
          )}
        </div>

        {/* Progress counters */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 12 }}>
          <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.04)', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{job?.files_found || 0}</div>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('accounting_restore.files_found')}</div>
          </div>
          <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.04)', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{job?.files_processed || 0}</div>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('accounting_restore.files_processed')}</div>
          </div>
          <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.04)', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{job?.invoices_created || 0}</div>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('accounting_restore.invoices_label')}</div>
          </div>
          <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.04)', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{job?.transactions_created || 0}</div>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('accounting_restore.transactions_label')}</div>
          </div>
        </div>
      </Section>

      {/* Questions from AI */}
      {job?.questions?.length > 0 && (
        <Section title={`❓ ${t('accounting_restore.questions_from_ai')}`}>
          {job.questions.map(q => (
            <div key={q.id} style={{
              padding: 14, borderRadius: 12, marginBottom: 10,
              background: q.status === 'answered' ? 'rgba(16,185,129,0.06)' : 'rgba(245,158,11,0.06)',
              border: `1px solid ${q.status === 'answered' ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)'}`,
            }}>
              {/* Context */}
              {q.context && q.context !== 'system' && (
                <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 6 }}>📎 {q.context}</div>
              )}
              {/* Question */}
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                🤖 {q.question}
              </div>
              {/* Answer or input */}
              {q.status === 'answered' ? (
                <div style={{ fontSize: 13, color: '#10b981', fontStyle: 'italic' }}>
                  ✅ {q.answer}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={answerText}
                    onChange={e => setAnswerText(e.target.value)}
                    placeholder={t('accounting_restore.your_answer')}
                    onKeyDown={e => e.key === 'Enter' && submitAnswer(q.id)}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => submitAnswer(q.id)}
                    disabled={answeringId === q.id}
                    style={{ minWidth: 70, borderRadius: 12, fontWeight: 700 }}
                  >
                    {answeringId === q.id ? '⏳' : '→'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
        {job?.status === 'completed' && (
          <button className="btn btn-primary" onClick={loadResults} style={{
            padding: 16, fontSize: 16, fontWeight: 800, borderRadius: 14,
          }}>
            📊 {t('accounting_restore.show_results')}
          </button>
        )}
        {['pending', 'downloading', 'parsing'].includes(job?.status) && (
          <button className="btn" onClick={async () => {
            try {
              const { data } = await api.get(`/owner/accounting/restore/${jobId}`);
              setJob(data);
            } catch {}
          }} style={{
            padding: 12, borderRadius: 12, border: '1px solid rgba(99,102,241,0.3)',
            background: 'rgba(99,102,241,0.1)', color: '#818cf8', cursor: 'pointer', fontWeight: 700,
          }}>
            🔄 {t('accounting_restore.refresh_status')}
          </button>
        )}
        <button onClick={() => { setTab('form'); setJob(null); setJobId(null); }} style={{
          padding: 10, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
          background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
        }}>← {t('common.back')}</button>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════
  // TAB: FORM
  // ═══════════════════════════════════════════════════
  return (
    <div className="page">
      <BackButton />
      <h1>📊 {t('accounting_restore.title')}</h1>
      <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 20, marginTop: -8 }}>
        {t('accounting_restore.subtitle')}
      </div>
      {msg && <div className="message" style={{ marginBottom: 12 }}>{msg}</div>}

      {/* Google Drive Link */}
      <Section title={`📁 ${t('accounting_restore.google_drive')}`}>
        <label style={labelStyle}>{t('accounting_restore.drive_link')} *</label>
        <input
          style={inputStyle}
          value={form.google_drive_url}
          onChange={e => upd('google_drive_url', e.target.value)}
          placeholder="https://drive.google.com/drive/folders/..."
        />
        <div style={{ fontSize: 11, opacity: 0.4, marginTop: 6 }}>
          {t('accounting_restore.drive_hint')}
        </div>
      </Section>

      {/* Company Type */}
      <Section title={`🏢 ${t('accounting_restore.company_type')}`}>
        <div style={{ display: 'flex', gap: 8 }}>
          {COMPANY_TYPES.map(ct => (
            <button key={ct.value} onClick={() => upd('company_type', ct.value)} style={{
              flex: 1, padding: '12px 8px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: form.company_type === ct.value ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
              color: form.company_type === ct.value ? '#818cf8' : 'rgba(255,255,255,0.6)',
              fontWeight: form.company_type === ct.value ? 800 : 500,
              borderBottom: form.company_type === ct.value ? '3px solid #6366f1' : '3px solid transparent',
              fontSize: 12, transition: 'all 0.2s',
            }}>
              {ct.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Period */}
      <Section title={`📅 ${t('accounting_restore.period_title')}`}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button onClick={() => upd('period_mode', 'year')} style={{
            flex: 1, padding: 10, borderRadius: 10, border: 'none', cursor: 'pointer',
            background: form.period_mode === 'year' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
            color: form.period_mode === 'year' ? '#10b981' : 'rgba(255,255,255,0.6)',
            fontWeight: 600, fontSize: 14,
          }}>📆 {t('accounting_restore.period_year')}</button>
          <button onClick={() => upd('period_mode', 'range')} style={{
            flex: 1, padding: 10, borderRadius: 10, border: 'none', cursor: 'pointer',
            background: form.period_mode === 'range' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
            color: form.period_mode === 'range' ? '#10b981' : 'rgba(255,255,255,0.6)',
            fontWeight: 600, fontSize: 14,
          }}>📅 {t('accounting_restore.period_range')}</button>
        </div>

        {form.period_mode === 'year' ? (
          <div>
            <label style={labelStyle}>{t('accounting_restore.period_year')}</label>
            <select style={selectStyle} value={form.year} onChange={e => upd('year', Number(e.target.value))}>
              {Array.from({ length: 5 }, (_, i) => currentYear - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>{t('accounting_restore.from')}</label>
              <input style={inputStyle} type="date" value={form.period_start}
                onChange={e => upd('period_start', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>{t('accounting_restore.to')}</label>
              <input style={inputStyle} type="date" value={form.period_end}
                onChange={e => upd('period_end', e.target.value)} />
            </div>
          </div>
        )}
      </Section>

      {/* Info */}
      <div style={{
        padding: 14, borderRadius: 12, marginBottom: 20,
        background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>🤖 {t('accounting_restore.ai_will_do')}</div>
        <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.6 }}>
          1. {t('accounting_restore.ai_step_1')}<br />
          2. {t('accounting_restore.ai_step_2')}<br />
          3. {t('accounting_restore.ai_step_3')}<br />
          4. {t('accounting_restore.ai_step_4')}<br />
          5. {t('accounting_restore.ai_step_5')}
        </div>
      </div>

      {/* Full Package Button */}
      <button className="btn" onClick={startPackage} disabled={pkgLoading} style={{
        width: '100%', padding: 16, fontSize: 16, fontWeight: 800, borderRadius: 14,
        marginBottom: 12, letterSpacing: '0.02em',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: '#fff', border: 'none', cursor: 'pointer',
      }}>
        {pkgLoading ? `⏳ ${t('accounting_restore.starting')}` : `📦 ${t('accounting_restore.generate_package')}`}
      </button>
      <div style={{ fontSize: 11, opacity: 0.4, marginBottom: 16, textAlign: 'center' }}>
        {t('accounting_restore.package_hint')}
      </div>

      {/* Start Button */}
      <button className="btn btn-primary" onClick={startRestore} disabled={loading} style={{
        width: '100%', padding: 16, fontSize: 16, fontWeight: 800, borderRadius: 14,
        marginBottom: 12, letterSpacing: '0.02em',
      }}>
        {loading ? `⏳ ${t('accounting_restore.starting')}` : `🚀 ${t('accounting_restore.start_restore')}`}
      </button>

      <button onClick={() => setPage('dashboard')} style={{
        width: '100%', padding: 10, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
        background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
        marginBottom: 32,
      }}>← {t('common.to_dashboard')}</button>
    </div>
  );
}
